
import React, { useState, useEffect } from 'react';
import './App.css';

// Types
interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  featured: boolean;
  image?: string;
  videoId?: string;
  affiliateProducts?: AffiliateProduct[];
}

interface AffiliateProduct {
  name: string;
  description: string;
  price: string;
  link: string;
  image: string;
  discount?: string;
}

interface YouTubeVideo {
  id: string;
  title: string;
  thumbnail: string;
  views: string;
  duration: string;
}

interface HeroSlide {
  id: number;
  title: string;
  subtitle: string;
  image: string;
  cta?: string;
}

// Hero slider content with marriage tips and quotes
const heroSlides: HeroSlide[] = [
  {
    id: 1,
    title: "Patience and mercy build strong bonds.",
    subtitle: "Remember, every couple faces challenges—grow together, not apart.",
    image: "https://i.ibb.co/Q3PPgy3W/banner.jpg",
    cta: "Start Your Journey"
  },
  {
    id: 2,
    title: "Communication is the bridge between two hearts.",
    subtitle: "Learn the art of listening and speaking with love and respect.",
    image: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=1200&auto=format&fit=crop",
    cta: "Improve Communication"
  },
  {
    id: 3,
    title: "Small daily habits create lasting love.",
    subtitle: "Discover the simple actions that strengthen your marriage every day.",
    image: "https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=1200&auto=format&fit=crop",
    cta: "Learn Daily Habits"
  },
  {
    id: 4,
    title: "Financial harmony starts with shared goals.",
    subtitle: "Build wealth together with trust, transparency, and teamwork.",
    image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=1200&auto=format&fit=crop",
    cta: "Financial Planning"
  },
  {
    id: 5,
    title: "Keep dating your spouse, always.",
    subtitle: "Romance isn't just for newlyweds—make every moment count.",
    image: "https://images.unsplash.com/photo-1474552226712-ac0f0961a954?w=1200&auto=format&fit=crop",
    cta: "Date Night Ideas"
  }
];

// Sample YouTube videos (marriage content)
const latestVideos: YouTubeVideo[] = [
  {
    id: "",
    title: "When Silence in Marriage Is Not Sabr",
    thumbnail: "./silenceNotSabr.jpg",
    views: "45K views",
    duration: "1:34"
  },
  {
    id: "",
    title: "Why Love Alone Is Not Enough",
    thumbnail: "./loveIsNotEnough.jpg",
    views: "32K views",
    duration: "8:45"
  },
  {
    id: "",
    title: "How We Handle Money as a Married Couple",
    thumbnail: "./couples.jpg",
    views: "67K views",
    duration: "15:22"
  },
  {
    id: "",
    title: "Conflict Resolution: What Actually Works",
    thumbnail: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=400&auto=format&fit=crop",
    views: "89K views",
    duration: "10:15"
  }
];

// Sample affiliate products
const recommendedProducts: AffiliateProduct[] = [
  {
    name: "The 5 Love Languages",
    description: "Discover your love language and transform your relationship",
    price: "$14.99",
    link: "#",
    image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=200&auto=format&fit=crop",
    discount: "20% OFF"
  },
  {
    name: "Couples Journal",
    description: "Daily prompts to strengthen your connection",
    price: "$24.99",
    link: "#",
    image: "https://images.unsplash.com/photo-1531346878377-a5be20888e57?w=200&auto=format&fit=crop"
  },
  {
    name: "Date Night Subscription Box",
    description: "Monthly date ideas delivered to your door",
    price: "$39.99/mo",
    link: "#",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=200&auto=format&fit=crop",
    discount: "First Month 50% OFF"
  }
];

// Sample blog posts - Marriage focused content
const samplePosts: BlogPost[] = [
  {
    id: 1,
    title: "When Silence in Marriage Is Not Sabr",
    excerpt: "Silence in marriage is often mistaken for sabr. Learn the Islamic difference between patience, emotional neglect, and mercy in marriage.",
    content: `<h2>🔹Introduction</h2>
      <p>Silence in marriage is often praised as sabr, especially in difficult moments. Many spouses believe that staying quiet is a sign of patience and faith. However, Islam does not teach emotional neglect under the name of sabr.
 In reality, silence in marriage can either be wisdom—or a slow destruction of emotional connection. Understanding the difference is crucial for anyone seeking a peaceful, Allah-centered marriage.
 </p>
      
       <div class="ad-placeholder"><img src="./sabrArticle.jpg" alt="Ad"/></div>
      
      <h2>Understanding Sabr in Islam</h2>
     <p>Sabr in Islam means patience with wisdom, restraint, and purpose. It is not suppression of pain, nor enduring harm in silence.

Allah praises sabr when it:
<ul>
<li>Preserves dignity</li>

<li>Prevents injustice</li>

<li>Leads to resolution</li>

<li>Draws a person closer to Allah</li>
 </ul><br>

 Sabr is active, not passive. It involves intention, prayer, and effort—not emotional withdrawal.
 </p>

       <h2>When Silence Becomes Harmful in Marriage</h2>

      <p>Silence in marriage is not sabr when it:
  <ol>
  <li>Avoids necessary communication</li>

 <li>Punishes a spouse emotionally</li>

  <li>Allows resentment to grow</li>

 <li>Replaces mercy with distance</li>
 </ol><br>

  Islam encourages dialogue rooted in kindness. Prolonged emotional silence can lead to neglect, misunderstanding, and spiritual disconnection between spouses.</p>
      
       <h2>The Islamic Difference Between Patience and Emotional Neglect</h2>
      <p>
      Sabr(Patience) Emotional Silence:
          <ul>
    <li>Seeks peace Avoids responsibility</li>
    <li>Guided by mercy Driven by resentment</li>
   <li>Allows communication Blocks emotional access</li>
    <li>Leads to healing Leads to distance</li>
    </ul><br>

 The Prophet ﷺ never taught believers to abandon emotional responsibility in marriage.
      </p>
      
      <div class="affiliate-box">

       <p><strong>Recommended Resource:</strong> The Marriage Insight Academy website helps facilitate meaningful conversations with your spouse.</p>
      </div>

      <h2>Mercy Is the Foundation of Islamic Marriage</h2>
      <p>
       Allah says He placed love and mercy between spouses (Qur’an 30:21).
 Mercy includes:
 <ul>
 <li>Listening</li>
 <li>Speaking gently</li>
 <li>Addressing issues early</li>
 <li>Making space for emotional safety</li>
 </ul><br>


 A marriage without communication slowly loses its mercy—even if both spouses are patient.
</p>
      
      <h2>What to Do Instead of Silent Endurance</h2>
      <p> 
    If silence is becoming your default response:
<ul>
<li> Reflect: Is this sabr or avoidance?</li> 

 <li> Make duʿāʾ for wisdom before speaking</li> 

 <li> Choose calm communication over withdrawal</li> 

 <li> Seek counsel if issues persist </li><br>

 Islam values peace through effort, not peace through emotional absence.
 </p>
      
       <div class="ad-placeholder"><img src="./dua.jpg" alt="Ad"/></div>
      
     <h2>🔹Conclusion</h2>
       <p>
      Silence in marriage is not always sabr. When silence replaces mercy, communication, and responsibility, it contradicts Islamic principles. True sabr nurtures the marriage, protects the heart, and seeks solutions with wisdom.
      A healthy Islamic marriage is not loud—but it is never emotionally empty.
       </p>`,
    author: "MIA Team",
    date: "December 30, 2025",
    category: "Marriage Tips",
    readTime: "5 min read",
    featured: true,
    image: "./sabrArticle.jpg",
    videoId: "dQw4w9WgXcQ",
    affiliateProducts: recommendedProducts.slice(0, 2)
  },
  {
    id: 2,
    title: "Why Love Alone Is Not Enough in Islamic Marriage",
    excerpt: "Love alone is not enough in Islamic marriage. Discover how mercy, responsibility, and faith sustain a lasting and peaceful marital life.",
    content: `


<h2>Introduction</h2>

<p>Love is often described as the foundation of marriage. Many couples enter marriage believing that as long as love exists, everything else will fall into place. However, in Islam, marriage is built on far more than emotions alone. Love alone is not enough in Islamic marriage because feelings can change, but values, responsibility, and mercy sustain a home through every season of life. Islam provides a complete framework that balances love with purpose, commitment, and accountability.</p>


<h2>Marriage in Islam Is More Than Romantic Love</h2>

<p>Islam does not deny love; rather, it places love within a structured system that protects both spouses.</p>

Allah says in the Qur’an:

 “And among His signs is that He created for you spouses from among yourselves so that you may find tranquility in them, and He placed between you affection and mercy.” (Qur’an 30:21)



This verse highlights three pillars:

<ul>
<li>Tranquility (Sakinah)</li>

<li>Affection (Mawaddah)</li>

<li>Mercy (Rahmah)</li>

</ul><br>

Romantic love falls under affection, but tranquility and mercy are equally essential. Without them, love alone becomes fragile.



Love Changes, Responsibility Remains

One reality many couples overlook is that emotions fluctuate. Love may feel strong at the beginning of marriage, but life pressures—finances, children, health, or unmet expectations—can affect emotions.

Islam prepares spouses for this reality by emphasizing:
<ul>
<li>Responsibility over feelings</li>

<li>Commitment over convenience</li>

<li>Patience over impulse</li>

</ul><br>


A marriage that relies only on love struggles when emotions fade. But a marriage built on responsibility survives emotional lows.
</p>


<h2>The Role of Mercy in Islamic Marriage</h2>

<p>Mercy is what keeps a marriage alive when love feels weak.

Mercy means:
<ul>
<li>Choosing kindness during conflict</li>

<li>Forgiving mistakes</li>

<li>Being gentle when your spouse is emotionally exhausted</li>

</ul><br>


The Prophet ﷺ never taught that spouses must always feel love. Instead, he taught how to act with mercy, even during difficulty.

When mercy is present:

<ul>
<li>Silence does not become emotional neglect</li>

Disagreements do not turn into cruelty

Weak moments do not destroy the bond

</ul><br>


This is why rahmah is central in Islamic marriage advice.</p>




<h2>Communication Matters More Than Feelings</h2>

<p>Many marriages fail not because love disappears, but because communication breaks down.

Islam encourages:

<ul>
<li>Honest dialogue</li>
<li>Emotional presence</li>
<li>Respectful disagreement</li>
</ul>

The Prophet ﷺ listened attentively to his wives and valued their emotions. Love without communication leads to misunderstanding, while communication nurtures understanding even when love feels distant.</p>


<h2>Rights and Duties Balance Love</h2>

<p>Islamic marriage is a contract with rights and responsibilities on both sides.

Love alone does not teach:
<ul>
<li>Financial responsibility</li>
<li>Emotional support</li>
<li>Mutual respect</li>
<li>Fair treatment</li>
</ul><br>

Islam does.

When spouses understand their roles:
<ul>
<li>Expectations become clearer</li>
<li>Conflicts reduce</li>
<li>Trust strengthens</li>
</ul><br>


Ignoring these duties while relying only on love often leads to resentment.</p>


<h2>Patience Is Stronger Than Passion</h2>

<p>Passion is intense but short-lived. Patience, however, carries a marriage through hardship.

Islam places great value on sabr (patience), especially in relationships. Patience helps spouses:

<ul>
<li>Endure difficult phases</li>
<li>Grow emotionally</li>
<li>Mature spiritually</li>
</ul><br>

A patient spouse remains committed even when love feels quiet. This is one of the deepest forms of worship within marriage.
</p>

Why Many Marriages Fail Despite Love

<h2> Why Many Marriages Fail Despite Love </h2>

<p>Many couples genuinely love each other, yet their marriages collapse. Common reasons include:
<ul>
<li>Lack of Islamic knowledge about marriage</li>
<li>Unrealistic expectations</li>
<li>Emotional silence</li>
</ul><br>

Absence of mercy and patience


This proves that love, without guidance, is insufficient.</p>




<h2>Building a Strong Islamic Marriage</h2>

<p>To build a lasting marriage, Islam encourages:

<ul>
<li>Love rooted in faith</li>
<li>Mercy during hardship</li>
<li>Clear communication</li>
<li>Fulfillment of rights</li>
<li>Constant self-improvement</li>
</ul><br>

When love is combined with Islamic principles, it becomes stable, mature, and rewarding.</p>




<h2> Conclusion </h2>

Love is a beautiful gift from Allah, but love alone is not enough in Islamic marriage. Islam teaches that mercy, responsibility, patience, and faith are what sustain a marriage when emotions rise and fall. A successful marriage is not defined by constant romance, but by consistent character, commitment, and compassion. When spouses seek Allah together, their marriage becomes stronger than feelings alone could ever make it.

If this reflection resonated with you, consider reading related articles on emotional silence in Islamic marriage.
    `,
    author: "MIA Team",
    date: "December 30, 2025",
    category: "Marriage Tips",
    readTime: "6 min read",
    featured: true,
    image: "./loveNotenough.jpg"
  },
  // {
  //   id: 3,
  //   title: "Keeping Romance Alive After Kids",
  //   excerpt: "Practical strategies for maintaining intimacy and connection when parenting takes center stage.",
  //   content: `Blog post content here...`,
  //   author: "Emily Thompson",
  //   date: "December 20, 2024",
  //   category: "Parenting",
  //   readTime: "8 min read",
  //   featured: false,
  //   image: "https://images.unsplash.com/photo-1474552226712-ac0f0961a954?w=800&auto=format&fit=crop",
  //   videoId: "dQw4w9WgXcQ"
  // },
  // {
  //   id: 4,
  //   title: "The Art of Fighting Fair in Marriage",
  //   excerpt: "Transform conflicts into opportunities for growth with these proven communication strategies.",
  //   content: `Blog post content here...`,
  //   author: "Dr. Michael Roberts",
  //   date: "December 18, 2024",
  //   category: "Communication",
  //   readTime: "6 min read",
  //   featured: false,
  //   image: "https://images.unsplash.com/photo-1523824921871-d6f1a15151f1?w=800&auto=format&fit=crop"
  // },
  // {
  //   id: 5,
  //   title: "Creating Rituals of Connection",
  //   excerpt: "Build meaningful traditions that strengthen your bond and create lasting memories together.",
  //   content: `Blog post content here...`,
  //   author: "Lisa Anderson",
  //   date: "December 15, 2024",
  //   category: "Relationships",
  //   readTime: "5 min read",
  //   featured: false,
  //   image: "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=800&auto=format&fit=crop"
  // }
];

const App: React.FC = () => {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSliding, setIsSliding] = useState(false);

  const categories = ['All', 'Marriage Tips', 'Communication', 'Finance', 'Parenting', 'Relationships'];

  const filteredPosts = samplePosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = samplePosts.filter(post => post.featured);

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      handleNextSlide();
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [currentSlide]);

  const handleNextSlide = () => {
    if (!isSliding) {
      setIsSliding(true);
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      setTimeout(() => setIsSliding(false), 500);
    }
  };

  const handlePrevSlide = () => {
    if (!isSliding) {
      setIsSliding(true);
      setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
      setTimeout(() => setIsSliding(false), 500);
    }
  };

  const goToSlide = (index: number) => {
    if (!isSliding && index !== currentSlide) {
      setIsSliding(true);
      setCurrentSlide(index);
      setTimeout(() => setIsSliding(false), 500);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-3">                         
                  <img src={'./logo.png'} alt="MIA Logo" className="w-12 h-12" />                      
                   </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => setSelectedPost(null)}
                className="text-gray-700 hover:text-navy-900 font-medium transition-colors"
              >
                Blog
              </button>
              <a 
                href="https://www.youtube.com/@MarriageInsightAcademy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-navy-900 font-medium transition-colors flex items-center gap-1"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
                YouTube
              </a>
              <button className="text-gray-700 hover:text-navy-900 font-medium transition-colors">
                Resources
              </button>
              <button className="text-gray-700 hover:text-navy-900 font-medium transition-colors">
                About
              </button>
              <button className="bg-gold-500 text-navy-900 px-5 py-2 rounded-full hover:bg-gold-400 transition-colors font-semibold">
                Free Marriage Guide
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <button 
                onClick={() => {setSelectedPost(null); setMobileMenuOpen(false);}}
                className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md font-medium"
              >
                Blog
              </button>
              <a 
                href="https://www.youtube.com/@MarriageInsightAcademy"
                className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md font-medium"
              >
                YouTube Channel
              </a>
              <button className="block w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-md font-medium">
                Resources
              </button>
              <button className="block w-full text-left px-3 py-2 bg-gold-500 text-navy-900 rounded-md font-semibold">
                Free Marriage Guide
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      {!selectedPost ? (
        // Home/Blog List View with Hero Slider
        <div>
          {/* Hero Section with Image Slider */}
          <section className="relative bg-gradient-to-b from-gray-50 to-white overflow-hidden">
            {/* Slider Container */}
            <div className="relative h-[600px] md:h-[700px]">
              {/* Slides */}
              <div className="relative h-full">
                {heroSlides.map((slide, index) => (
                  <div
                    key={slide.id}
                    className={`absolute inset-0 transition-all duration-500 ${
                      index === currentSlide 
                        ? 'opacity-100 translate-x-0' 
                        : index < currentSlide 
                        ? 'opacity-0 -translate-x-full' 
                        : 'opacity-0 translate-x-full'
                    }`}
                  >
                    {/* Background Image */}
                    <div 
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${slide.image})` }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
                    </div>

                    {/* Content */}
                    <div className="relative h-full flex items-center justify-center">
                      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        {/* Title */}
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 drop-shadow-lg animate-fade-in">
                          Marriage Insights & Advice
                        </h1>
                        
                        {/* Quote Box */}
                        <div className="max-w-3xl mx-auto bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-2xl animate-slide-up">
                          <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mb-4">
                            "{slide.title}"
                          </h2>
                          <p className="text-lg text-gray-700 mb-6">
                            {slide.subtitle}
                          </p>
                          
                          {/* Slide Indicators */}
                          <div className="flex justify-center space-x-2 mb-6">
                            {heroSlides.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => goToSlide(idx)}
                                className={`h-2 transition-all duration-300 ${
                                  idx === currentSlide 
                                    ? 'w-8 bg-gold-500' 
                                    : 'w-2 bg-gray-400 hover:bg-gray-600'
                                } rounded-full`}
                                aria-label={`Go to slide ${idx + 1}`}
                              />
                            ))}
                          </div>

                          {/* CTAs */}
                          <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button className="px-8 py-3 bg-gold-500 text-navy-900 font-semibold rounded-full hover:bg-gold-400 transition-colors">
                              Get Free Marriage Guide
                            </button>
                            <a 
                              href="https://www.youtube.com/@MarriageInsightAcademy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-8 py-3 bg-navy-900 text-white font-semibold rounded-full hover:bg-navy-800 transition-colors flex items-center justify-center gap-2"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                              </svg>
                              Watch on YouTube
                            </a>
                          </div>
                        </div>

                        {/* Search Bar */}
                        <div className="mt-8 max-w-xl mx-auto">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search marriage topics..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="w-full px-6 py-4 pr-12 rounded-full border border-white/30 bg-white/90 backdrop-blur-sm focus:outline-none focus:border-gold-500 focus:bg-white transition-all"
                            />
                            <svg className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={handlePrevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors group"
                aria-label="Previous slide"
              >
                <svg className="w-6 h-6 text-navy-900 group-hover:text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={handleNextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/80 backdrop-blur-sm rounded-full hover:bg-white transition-colors group"
                aria-label="Next slide"
              >
                <svg className="w-6 h-6 text-navy-900 group-hover:text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </section>

          {/* YouTube Videos Section */}
          <section className="py-12 bg-navy-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl font-bold text-white">Latest YouTube Videos</h2>
                <a 
                  href="https://www.youtube.com/@MarriageInsightAcademy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold-500 hover:text-gold-400 font-medium"
                >
                  View All →
                </a>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {latestVideos.map((video) => (
                  <a
                    key={video.id}
                    href={`https://youtube.com/watch?v=${video.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="relative rounded-lg overflow-hidden">
                      <img 
                        src={video.thumbnail} 
                        alt={video.title}
                        className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                        <svg className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    <h3 className="mt-3 text-white font-medium group-hover:text-gold-500 transition-colors line-clamp-2">
                      {video.title}
                    </h3>
                    <p className="text-gray-400 text-sm mt-1">{video.views}</p>
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* Category Filter */}
          <section className="border-b border-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center space-x-8 overflow-x-auto py-4">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      selectedCategory === category
                        ? 'border-gold-500 text-navy-900'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Posts */}
          {selectedCategory === 'All' && (
            <section className="py-12">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-navy-900 mb-8">Featured Articles</h2>
                <div className="grid md:grid-cols-2 gap-8">
                  {featuredPosts.map(post => (
                    <article 
                      key={post.id}
                      onClick={() => setSelectedPost(post)}
                      className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow cursor-pointer overflow-hidden group border border-gray-100"
                    >
                      {post.image && (
                        <div className="h-48 overflow-hidden relative">
                          <img 
                            src={post.image} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                          {post.videoId && (
                            <div className="absolute top-3 right-3 bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                              VIDEO
                            </div>
                          )}
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center space-x-4 mb-3">
                          <span className="inline-block px-3 py-1 bg-gold-50 text-gold-700 rounded-full text-xs font-medium">
                            {post.category}
                          </span>
                          <span className="text-gray-500 text-sm">{post.readTime}</span>
                        </div>
                        <h3 className="text-2xl font-bold text-navy-900 mb-3 group-hover:text-gold-600 transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-navy-900 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">
                                {post.author.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div className="text-sm">
                              <p className="text-gray-900 font-medium">{post.author}</p>
                              <p className="text-gray-500">{post.date}</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gold-600 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* All Posts Grid */}
          <section className="py-12 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-navy-900 mb-8">
                {selectedCategory === 'All' ? 'Latest Articles' : `${selectedCategory} Articles`}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map(post => (
                  <article 
                    key={post.id}
                    onClick={() => setSelectedPost(post)}
                    className="bg-white rounded-xl border border-gray-100 hover:border-gold-200 hover:shadow-md transition-all cursor-pointer group"
                  >
                    {post.image && (
                      <div className="h-48 overflow-hidden rounded-t-xl relative">
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {post.videoId && (
                          <div className="absolute top-3 right-3 bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold">
                            VIDEO
                          </div>
                        )}
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                          {post.category}
                        </span>
                        <span className="text-gray-500 text-sm">{post.readTime}</span>
                      </div>
                      <h3 className="text-xl font-bold text-navy-900 mb-2 group-hover:text-gold-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                          <span>{post.date}</span>
                        </div>
                        <svg className="w-4 h-4 text-gold-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Newsletter Section */}
          <section className="bg-navy-900 py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-2xl mx-auto text-center">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Get Your Free Marriage Transformation Guide
                </h2>
                <p className="text-gray-300 mb-8">
                  Join 10,000+ couples receiving weekly tips and our exclusive 30-page guide to building a stronger marriage.
                </p>
                <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gold-500 text-navy-900 font-semibold rounded-lg hover:bg-gold-400 transition-colors"
                  >
                    Get Free Guide
                  </button>
                </form>
                <p className="text-xs text-gray-400 mt-4">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </div>
            </div>
          </section>
        </div>
      ) : (
        // Blog Post View (simplified for brevity)
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button
            onClick={() => setSelectedPost(null)}
            className="flex items-center text-navy-900 hover:text-gold-600 mb-8 font-medium transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Articles
          </button>

          {selectedPost.image && (
            <img 
              src={selectedPost.image} 
              alt={selectedPost.title}
              className="w-full h-96 object-cover rounded-2xl mb-8"
            />
          )}

          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-gold-50 text-gold-700 rounded-full text-sm font-medium mb-4">
              {selectedPost.category}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4 leading-tight">
              {selectedPost.title}
            </h1>
            <div className="flex items-center text-gray-600 space-x-4">
              <span>{selectedPost.author}</span>
              <span>•</span>
              <span>{selectedPost.date}</span>
              <span>•</span>
              <span>{selectedPost.readTime}</span>
            </div>
          </div>

          <div 
            className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: selectedPost.content }}
          />
        </article>
      )}

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center space-x-3">
                  <div>                         
                  <img src={'./logo.png'} alt="MIA Logo" className="w-12 h-12" />                      
                   </div>
              </div>
              
              
              </div>
              <p className="text-gray-600 text-sm">
                Helping couples build stronger, happier marriages through practical advice and real-life wisdom.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-navy-900 mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-600 hover:text-navy-900 text-sm">Marriage Guide</a></li>
                <li><a href="#" className="text-gray-600 hover:text-navy-900 text-sm">Communication Workbook</a></li>
                <li><a href="#" className="text-gray-600 hover:text-navy-900 text-sm">Date Night Ideas</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-navy-900 mb-4">Categories</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-600 hover:text-navy-900 text-sm">Marriage Tips</a></li>
                <li><a href="#" className="text-gray-600 hover:text-navy-900 text-sm">Communication</a></li>
                <li><a href="#" className="text-gray-600 hover:text-navy-900 text-sm">Finance</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-navy-900 mb-4">Connect</h4>
              <div className="flex space-x-3">
                <a href="https://www.youtube.com/@MarriageInsightAcademy" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-red-100 transition-colors">
                  <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-blue-100 transition-colors">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200">
            <p className="text-center text-gray-600 text-sm">
              © 2024 MIA. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;